/**
 * Resolves the caller's app role via GET /api/me and gates the entire UI:
 *
 *   - resolving → "Checking access…" splash (mirrors AuthGuard's)
 *   - fetch failure → retryable error screen (fail closed — no UI leaks)
 *   - role "none" → full-screen access-denied with sign-out (only an older
 *     API deployment returns "none"; the current API defaults every
 *     authenticated caller to "user")
 *   - user/admin → children, role available via useRole()
 *
 * The env-specific Okta group names live ONLY in the backend (infra/bin/
 * app.ts → Lambda env → handlers/common/authz.ts); the bundle never embeds
 * them — it just asks the API what the caller is. Hiding buttons here is
 * UX, not security: every write route is independently admin-guarded
 * server-side.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { AppRole } from "@canvas-sync/shared";
import { api } from "../api/client";
import { useAuth } from "./authContext";
import { RoleContext, type RoleContextValue } from "./roleContext";

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user, email, signOut } = useAuth();
  const [role, setRole] = useState<AppRole | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const me = await api.getMe();
      setRole(me.role);
    } catch (e) {
      setRole(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  // Re-resolve whenever the signed-in subject changes (sign-out → sign-in
  // as someone else must never keep the previous user's role).
  const sub = (user?.profile.sub as string | undefined) ?? null;
  useEffect(() => {
    setRole(null);
    if (sub) void load();
  }, [sub, load]);

  const value = useMemo<RoleContextValue>(
    () => ({ role: role ?? "none", isAdmin: role === "admin" }),
    [role],
  );

  if (error) {
    return (
      <RoleScreen title="Couldn't verify your access">
        <p style={detailStyle}>{error}</p>
        <div style={rowStyle}>
          <button style={buttonStyle} onClick={() => void load()}>
            Retry
          </button>
          <button style={buttonStyle} onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </RoleScreen>
    );
  }

  if (role === null) {
    return (
      <RoleScreen title="">
        <span>Checking access…</span>
      </RoleScreen>
    );
  }

  if (role === "none") {
    return (
      <RoleScreen title="No access to Canvas Sync">
        <p style={detailStyle}>
          {email ? `${email} isn't` : "Your account isn't"} in a Canvas Sync
          access group. Ask an administrator to add you to the viewer or admin
          Okta group, then sign in again.
        </p>
        <div style={rowStyle}>
          <button style={buttonStyle} onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </RoleScreen>
    );
  }

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

const detailStyle: CSSProperties = {
  maxWidth: 420,
  textAlign: "center",
  lineHeight: 1.6,
  margin: 0,
};

const rowStyle: CSSProperties = {
  display: "flex",
  gap: 12,
};

const buttonStyle: CSSProperties = {
  background: "transparent",
  border: "1px solid var(--text-mid)",
  borderRadius: 4,
  color: "var(--text-mid)",
  cursor: "pointer",
  fontFamily: "var(--mono)",
  fontSize: 12,
  letterSpacing: "0.04em",
  padding: "8px 16px",
};

function RoleScreen({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-0)",
        color: "var(--text-mid)",
        fontFamily: "var(--mono)",
        fontSize: 13,
        letterSpacing: "0.04em",
      }}
    >
      {title && (
        <div style={{ fontSize: 15, color: "var(--text-hi, inherit)" }}>
          {title}
        </div>
      )}
      {children}
    </div>
  );
}
